import * as React from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';

import Tooltip from 'af-components/Tooltip';

import type { CellError } from '../../error.types';
import styles from '../styles.module.scss';
import { TEXT_AREA_LINE_HEIGHT } from '../TextAreaCell';

type FieldValue = string | number | undefined;

interface Props {
	elementKey: string;
	value: FieldValue;
	name: string;
	placeholder?: string;
	className?: string;
	error?: CellError;
}

export const DisplayCell: React.FC<Props> = React.memo((props) => {
	const {
		placeholder,
		elementKey,
		className,
		value,
		name,
		error,
	} = props;

	const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
	const [rows, setRows] = React.useState<number>(1);

	// QA id is automatically generated to help with automated tests
	// We want to avoid accidentally forgetting to place an id on an element, making it harder for QA to write automated tests, so we keep it generated
	// Styling should never come from id, anyway
	const id = React.useMemo(() => (
		`accqa__textarea__display__${elementKey}`
	), [elementKey]);

	const getClassName = React.useMemo(() => {
		let cn = className ?? `${styles.textarea}`;

		if (error) {
			cn = `${cn} ${styles['with-error']}`;
			if (error.type === 'tooltip') {
				cn = `${cn} ${styles['with-tooltip']}`;
			} else if (error.type === 'required') {
				cn = `${cn} ${styles['error-placeholder']}`;
			}
		}
		return cn;
	}, [className, error]);

	React.useEffect(() => {
		const textarea = document.getElementById(id);

		const lineHeight = TEXT_AREA_LINE_HEIGHT;
		const scrollHeight = textarea?.scrollHeight ?? 0;
		const _rows = Math.floor(scrollHeight / lineHeight);
		if (_rows !== rows) {
			setRows(_rows);
		}
	}, [id, rows]);

	const renderCell = React.useMemo(() => {
		const placeholderInCell = error?.type === 'required'
			? error?.message
			: placeholder
				? placeholder
				: '-';

		return (
			<InputGroup className={`${styles['textarea-wrapper']}`}>
				<FormControl
					as="textarea"
					className={getClassName}
					defaultValue={value}
					id={id}
					key={elementKey}
					name={name}
					placeholder={placeholderInCell}
					ref={textAreaRef}
					rows={rows}
				/>
				{error?.type === 'tooltip' && error.message &&
					<Tooltip message={error.message} placement="auto">
						<span className="icon-notification_status" />
					</Tooltip>
				}
			</InputGroup>
		);
	}, [error, placeholder, getClassName, value, id, elementKey, name, rows]);

	return renderCell;
});
